import { uniq } from 'lodash-es'

import { track } from '@/common/pendo/agent'

function checklistTasksHaveSameObserversRights (tasks) {
  const tasksReadRightsAsJsonString = tasks.map(task => {
    const groups = task.rights.observers.groups?.map(group => group.id).sort()
    const users = task.rights.observers.users?.map(user => user.id).sort()
    return JSON.stringify({ groups, users })
  })

  return uniq(tasksReadRightsAsJsonString).length === 1
}

function checklistTasksHaveSameResponsiblesRights (tasks) {
  const tasksResponsiblesRightsAsJsonString = tasks.map(task => {
    const groups = task.rights.responsibles.groups?.map(group => group.id).sort()
    const users = task.rights.responsibles.users?.map(user => user.id).sort()
    return JSON.stringify({ groups, users })
  })

  return uniq(tasksResponsiblesRightsAsJsonString).length === 1
}

function checklistTasksHaveSameValidatorsRights (tasks) {
  const tasksValidatorRightsAsJsonString = tasks.map(task => {
    const groups = task.rights.validators.groups?.map(group => group.id).sort()
    const users = task.rights.validators.users?.map(user => user.id).sort()
    return JSON.stringify({ groups, users })
  })

  return uniq(tasksValidatorRightsAsJsonString).length === 1
}

/**
 * Get the details of a status from information on a checklist task and the categories
 * @param {Number} statusId The id of the status we want to search details for
 * @param {String} statusCategory The category of the status we want to search details for
 * @param {Array} taskStatusCategories The status categories as received from the back-end
 * @returns {object} With more informations on the status
 */
export function getStatusDetailsByTaskInformation (statusId, statusCategory, taskStatusCategories) {
  const statusesInCategory = taskStatusCategories[statusCategory]
  return statusesInCategory
    ? statusesInCategory.find(label => label.id === statusId)
    : null
}

export function checklistTasksHaveSameRights (tasks) {
  return checklistTasksHaveSameObserversRights(tasks) &&
    checklistTasksHaveSameResponsiblesRights(tasks) &&
    checklistTasksHaveSameValidatorsRights(tasks)
}

export function splitChecklistStructureAndDetails (checklist) {
  const details = {
    sections: [],
    tasks: [],
  }
  const mapChecklistItemStructure = item => {
    details[item.type === 'title' ? 'sections' : 'tasks'].push({
      id: item.id,
      title: item.title,
      depth: item.depth,
      positionDisplay: item.positionDisplay,
      isAwaitingValidation: item.isAwaitingValidation,
    })

    return {
      id: item.id,
      type: item.type,
      children: item.children.map(mapChecklistItemStructure),
    }
  }

  const structure = checklist.map(mapChecklistItemStructure)

  return {
    structure,
    details,
  }
}

export function highlightMovedItem(element, duration = 2000) {
  if (!element) return

  const elements = [element, ...element.querySelectorAll('*')]
  
  // Apply highlight
  for (const el of elements) {
    el.style.transition = 'background-color 0.3s ease'
    el.style.backgroundColor = 'rgb(var(--v-theme-darkblue-50))'
  }
  
  // Remove highlight after duration
  setTimeout(() => {
    for (const el of elements) {
      el.style.backgroundColor = ''
      setTimeout(() => el.style.transition = '', 300)
    }
  }, duration)
}

export async function scrollToMovedItemById (movedItemId, targetItemId) {
  const movedItem = document.getElementById(movedItemId)
  if (movedItem) {
    await scrollToItem(movedItem)
    highlightMovedItem(movedItem)
  } else {
    const targetItem = document.getElementById(targetItemId)
    if (targetItem) {
      await scrollToItem(targetItem)
      highlightMovedItem(targetItem)
    }
  }
}

export async function scrollToAddedItem (newAddedItem) {
  await scrollToItem(newAddedItem)
}

export async function scrollToItem (targetItem) {
  await waitForElement('.checklist-table')
  await waitForElement(() => document.body.contains(targetItem))

  // Wait for a render frame to ensure layout is up-to-date
  await new Promise(resolve => requestAnimationFrame(resolve))

  const checklistTable = document.querySelector('.checklist-table')
  if (!checklistTable) return

  const elementTop = targetItem.offsetTop
  const elementHeight = targetItem.offsetHeight
  const containerHeight = checklistTable.clientHeight

  const scrollTarget = elementTop - (containerHeight / 2) + (elementHeight / 2)

  checklistTable.scrollTo({
    top: scrollTarget,
  })
}

export function waitForElement(selectorOrFn, interval = 100, maxAttempts = 50) {
  return new Promise((resolve, reject) => {
    let attempts = 0
    const timer = setInterval(() => {
      let el

      if (typeof selectorOrFn === 'function') {
        el = selectorOrFn()
      } else {
        el = document.querySelector(selectorOrFn)
      }

      if (el) {
        clearInterval(timer)
        resolve(el)
      } else if (++attempts > maxAttempts) {
        clearInterval(timer)
        reject(
          new Error(
            `Timeout waiting for ${
              typeof selectorOrFn === 'string'
                ? selectorOrFn
                : '[predicate function]'
            }`,
          ),
        )
      }
    }, interval)
  })
}

export function trackChecklistCreation(state, mnemo) {
  if (state.checklistPristine) {
    track('create_checklist', {
      project_id: mnemo,
    });
  }
}